import React, { useState, useEffect, Suspense, useRef } from 'react';
import Loading from './components/Loading.jsx';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { ChatProvider } from './context/ChatContext.js';
import { WorkspaceProvider } from './context/WorkspaceContext.js';
import { listUsers } from './api/users.js';
import PaymentSuccess from './pages/PaymentSuccess.jsx';
import UserRoleTest from './pages/UserRoleTest.jsx';
import WorkspaceRouter from './pages/WorkspaceRouter.jsx';
import FreelancerWorkSpace from './components/FreelancerWorkSpace.jsx';
import CreatorWorkSpace from './components/CreatorWorkSpace.jsx';
import EntrepreneurWorkSpace from './components/EntrepreneurWorkSpace.jsx';
import StudentWorkSpace from './components/StudentWorkSpace.jsx';
import InfluencerWorkspace from './components/InfluencerWorkspace.jsx';
import BillingPlans from './components/BillingPlans.jsx';
import AdminDashboard from './modules/Dashboard/AdminDashboard.jsx';
import AdminAccessGate from './modules/Dashboard/AdminAccessGate.jsx';
import Dashboard from './modules/Dashboard/Dashboard.jsx';
import Home from './pages/Home.js';
import LandingPage from './pages/LandingPage.js';
import About from './pages/About.js';
import Pricing from './pages/Pricing.js';
import Profile from './pages/Profile.js';
import SearchBar from './modules/SearchBar/SearchBar_fixed.js';
import CustomSearchModal from './modules/SearchBar/CustomSearchModal_fixed.js';
import UserTrackingService from './services/userTrackingService.js';
import BigDataCloudService from './services/bigDataCloudService.js';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import Register from './pages/Register.jsx';
import Login from './pages/Login.jsx';
import Monetize from './pages/Monetize.jsx';
import GetReferralLink from './components/GetReferralLink.jsx';
import ReferralEarnings from './components/ReferralEarnings.jsx';
import AnonymousTest from './components/AnonymousTest.jsx';
import ChatRoom from './components/ChatRoom.jsx';
import PrivateRoute from './modules/Dashboard/PrivateRoute.jsx';
import PaymentPage from './modules/Payments/PaymentPage.jsx';
import Navbar from './modules/Navbar/Navbar.js';
import Footer from './modules/Footer/Footer.js';
import Sidebar from './modules/Sidebar/Sidebar.js';
import PromptBar from './components/PromptBar.js';
import ChatbotButton from './modules/ChatBot/ChatbotButton.jsx';
import CookieBanner from './modules/Cookies/CookiesBanner.jsx';
import ProPlanSelector from './modules/Subscription Plan/ProPlanSelector.jsx';
import TryProPage from './modules/Subscription Plan/try-pro.jsx';
import FreePlanRegister from './modules/Subscription Plan/FreePlanRegister.jsx';
import RegisterTest from './pages/RegisterTest.jsx';
import AuthGoogle from './pages/AuthGoogle.jsx';
import userTrackingService from './services/userTrackingService.js';
import UpgradeMode from './components/UpgradeMode.jsx';
import AnalyticsDashboard from './modules/Analytics/AnalyticsDashboard.jsx';

function UsersList() {
  const { user, isAuthenticated } = useAuth();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAuthenticated()) {
        setUsers([]);
        return;
      }
      try {
        const data = await listUsers();
        setUsers(data.users);
        console.log('List users:', data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, [user]);

  return null; // Or render users if needed
}

function AppContent() {
  const [isUpgradeMode, setIsUpgradeMode] = useState(false);

  const { user, isAuthenticated } = useAuth();

  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setIsUpgradeMode(true), 180000);
  };

  useEffect(() => {
    // Initialize tracking services on app start
    if (isAuthenticated()) {
      userTrackingService.init(user.id);
      // Optionally, initialize bigDataCloudService or other setup here
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    // Start the timer on mount
    resetTimer();
    // Add event listeners for user activity
    const events = ['mousemove', 'keypress', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (isUpgradeMode) {
    return <UpgradeMode />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <UsersList />
      <WorkspaceProvider>
        <ChatProvider>
          <Router>
          <div className="App">
            <header>
              <Navbar />
            </header>
            <main>
              <SearchBar onSearch={(query) => console.log("User searched for:", query)} />
              <ChatbotButton />

              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/home" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/Sidebar" element={<Sidebar />} />
                <Route path="/PromptBar" element={<PromptBar />} />
                <Route path="/Register" element={<Register />} />
                <Route path="/register-test" element={<RegisterTest />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/google" element={<AuthGoogle />} />
                <Route path="/cookies" element={<CookieBanner />} />
                <Route path="/ChatbotButton" element={<ChatbotButton />} />
                <Route path="/ProPlanSelector" element={<ProPlanSelector />} />
                <Route path="/select-plan/pro" element={<ProPlanSelector />} />
                <Route path="/try-pro" element={<TryProPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/Monetize" element={<Monetize />} />
                <Route path="/FreePlanRegister" element={<FreePlanRegister />} />
                <Route path="/GetReferralLink" element={<GetReferralLink />} />
                <Route path="/referral-link" element={<GetReferralLink />} />
                <Route path="/referral-earnings" element={<ReferralEarnings />} />
                <Route path="/anonymous-test" element={<AnonymousTest />} />
                <Route path="/payment/:planType/:billingPeriod" element={<PaymentPage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/workspace" element={<WorkspaceRouter />} />
                <Route path="/FreelancerWorkSpace" element={<FreelancerWorkSpace />} />
                <Route path="/EntrepreneurWorkSpace" element={<EntrepreneurWorkSpace />} />
                <Route path="/CreatorWorkSpace" element={<CreatorWorkSpace />} />
                <Route path="/StudentWorkSpace" element={<StudentWorkSpace />} />
                <Route path='/InfluencerWorkspace' element={<InfluencerWorkspace />} />
                <Route path='/BillingPlans' element={<BillingPlans />} />
                <Route path="/Test" element={<UserRoleTest />} />
                <Route
                  path="/admin-dashboard"
                  element={
                    <PrivateRoute>
                      <AdminDashboard />
                    </PrivateRoute>
                  }
                />
                <Route path="/Admin" element={<AdminAccessGate />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/analytics" element={<AnalyticsDashboard />} />
              </Routes>
              <CookieBanner />
            </main>
            <Footer />
          </div>
        </Router>
        </ChatProvider>
      </WorkspaceProvider>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
